import { Component, OnDestroy, OnInit } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { SHARED_DATE_FORMATS } from '../shared/shared-date-formats.constant';
import moment from 'moment';
import { Subject, throwError, zip } from 'rxjs';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TimeAllocationService } from './time-allocation.service';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { DemographicsService } from '../demographics/demographics.service';
import { Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedModulesModule } from '../shared/shared-modules.module';
import { SharedComponentsModule } from '../shared/shared-components.module';
import { SharedPipesModule } from '../shared/shared-pipes.module';
import { TimeAllocationLocation, TimeAllocationWorkedService, TimeAllocationRole, TIME_ALLOCATION_CONSTANTS } from '../shared/time-allocation.interface';
import { ApplicationInsightsService } from '../app-insights.service';

@Component({
  selector: 'app-time-allocation',
  templateUrl: './time-allocation.component.html',
  styleUrls: ['./time-allocation.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: SHARED_DATE_FORMATS,
    },
  ],
  imports: [
    SharedModulesModule,
    SharedComponentsModule,
    SharedPipesModule,
    RouterLink,
  ],
})
export class TimeAllocationComponent implements OnInit, OnDestroy {

  private unsubscriber: Subject<void> = new Subject<void>();
  allocationForm: UntypedFormGroup;
  loading = false;
  user;

  allocationID: number;
  allocationLocations: TimeAllocationLocation[];
  allocationServices: TimeAllocationWorkedService[];
  allocationRoles: TimeAllocationRole[];
  showRoles = false;

  constructor(public fb: UntypedFormBuilder,
    public timeAllocationService: TimeAllocationService,
    public demoService: DemographicsService,
    private appInsights: ApplicationInsightsService,
    private router: Router,
    private snackbar: MatSnackBar) { }

  ngOnInit(): void {
    this.loading = true;
    this.allocationID = history.state.allocationID;
    this.timeAllocationService.getTimeAllocationLocations().subscribe(locations => {
      this.allocationLocations = locations;
    });
    this.timeAllocationService.getTimeAllocationServices().subscribe(services => {  
      this.allocationServices = services;
    });
    this.timeAllocationService.getTimeAllocationRoles().subscribe(roles => {  
      this.allocationRoles = roles;
    });
    this.demoService.getUserData().pipe(
      takeUntil(this.unsubscriber),
      catchError(err => {
        this.appInsights.logEvent('Time Allocation Get User Data GET_ERROR', { 
          error: err, user_id: this.user.employee_id });
        return throwError(() => err);
      })).subscribe(user => {
        this.user = user;
        this.appInsights.logPageView('Time Allocation', this.router.url, { user_id: this.user.employee_id });
        zip(this.demoService.getProfile(user.employee_id),).pipe(
        takeUntil(this.unsubscriber),
        catchError(err => {
          this.appInsights.logEvent('Time Allocation Get Employee Profile GET_ERROR', { 
            error: err, user_id: this.user.employee_id });
          return throwError(() => err);
        }),
        finalize(() => this.loading = false)).subscribe(() => {
          this.setupAllocationForm();
        });
      });
  }

  setupAllocationForm() {
    this.allocationForm = this.fb.group({
      location: ['', Validators.required],
      serviceDate: ['', Validators.required],
      service:  ['', Validators.required],
      role:  [null],
      hoursWorked: [null, [Validators.required, allocatedHoursValidator()]],
      hoursTraveled: [null, allocatedHoursValidator()],
      note: [null],
    });

    if (this.allocationID) {
      this.timeAllocationService.getTimeAllocation(this.allocationID).pipe(
      takeUntil(this.unsubscriber),
      catchError((err => {
        this.appInsights.logEvent('Time Allocation Get GET_ERROR', { 
            error: err, allocationID: this.allocationID, user_id: this.user.employee_id });
        return throwError(() => err);
      })
      )).subscribe(result => {
        this.appInsights.logEvent('Time Allocation Get GET', { 
            allocationID: this.allocationID, user_id: this.user.employee_id });
        this.allocationForm.patchValue({
          location: result.locationID,
          serviceDate: moment(result.serviceDate, SHARED_DATE_FORMATS.parse.dbInput),
          service: this.allocationServices.find(svc => svc.allocationServiceID === result.allocationServiceID),
          role: result.allocationRoleID,
          hoursWorked: result.hoursWorked,
          hoursTraveled: result.hoursTraveled,
          note: result.allocationNote,
      })
      this.locationChanged({value: this.allocationForm.value.location});
      this.serviceChanged({value: this.allocationForm.value.service});
      });
    }
  }

  locationChanged($event) {
    if ($event.value === TIME_ALLOCATION_CONSTANTS.locationID.managementLocation) {
      const lockedService = this.allocationServices.find(svc => 
        svc.allocationServiceID === TIME_ALLOCATION_CONSTANTS.serviceID.managementLockedService);
      this.allocationForm.controls['service'].disable();
      this.allocationForm.controls['service'].setValue(lockedService);
      this.serviceChanged({value: lockedService});
    }
    else {
      this.allocationForm.controls['service'].enable();
    }
  }

  serviceChanged($event) {
    if ($event.value.allocationServiceID === TIME_ALLOCATION_CONSTANTS.serviceID.roleRequired) {
      this.showRoles = true;
      this.allocationForm.controls['role'].setValidators([Validators.required]);
    }
    else {
      this.showRoles = false;
      this.allocationForm.get('role').reset();
      this.allocationForm.controls['role'].clearValidators();
      this.allocationForm.controls['role'].updateValueAndValidity();
    }
  }

  onSubmit(form: UntypedFormGroup): void {
    this.loading = true;
    const raw = form.getRawValue();

    const data = 
      {
        allocationID: this.allocationID,
        employeeID: this.user.employee_id,
        locationID: raw.location,
        serviceDate: raw.serviceDate,
        hoursWorked: Number(raw.hoursWorked),
        hoursTraveled: raw.hoursTraveled === '' ? null : Number(raw.hoursTraveled),
        allocationNote: raw.note === '' ? null : raw.note,
        allocationServiceID: raw.service.allocationServiceID,
        allocationRoleID: raw.role,
      };

      if (this.allocationID) {
        this.timeAllocationService.editTimeAllocation(data).pipe(
          takeUntil(this.unsubscriber),
          finalize(() => { this.loading = false; }),
          catchError(err => {
            this.appInsights.logEvent('Time Allocation Edit PUT_ERROR', { 
              error: err, allocationID: this.allocationID, user_id: this.user.employee_id });
            this.snackbar.open('Edit Time Allocation ERROR', 'Dismiss');
            return throwError(() => err);
          })
        ).subscribe((r) => {
          this.appInsights.logEvent('Time Allocation Edit PUT', { 
            allocationID: this.allocationID, user_id: this.user.employee_id });
          this.snackbar.open('Edit Time Allocation Success', 'Dismiss');
          this.allocationID = 0;
          window.history.replaceState(null, '', '/time-allocation');
          form.reset();
          }
        );
      }
      else {
        this.timeAllocationService.createTimeAllocation(data).pipe(
          takeUntil(this.unsubscriber),
          finalize(() => { this.loading = false; }),
          catchError(err => {
            this.appInsights.logEvent('Time Allocation Create POST_ERROR', { 
              error: err, user_id: this.user.employee_id });
            this.snackbar.open('Create Time Allocation ERROR', 'Dismiss');
            return throwError(() => err);
          })
        ).subscribe((r) => {
          this.appInsights.logEvent('Time Allocation Create POST', { 
            user_id: this.user.employee_id });
          this.snackbar.open('Create Time Allocation Success', 'Dismiss');
          form.reset();
          }
        );
      }
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}

// Verify Allocated Hours for Work/Travel are between .5 and 24.
function allocatedHoursValidator(): ValidatorFn {
  return (c: AbstractControl): { [key: string]: boolean } | null => {
    const valueAsNumber = Number(c.value);
    if (!c.value || Number.isNaN(valueAsNumber)) {
      return null;
    }
    const valid = valueAsNumber >= 0.5 && valueAsNumber <= 24;
    return !valid ? { 'allocatedHours': true } : null;
  };
}